// locationSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { deleteTeamsData, fetchLocationBySearch, fetchLocationDataStatus, updateLocationData } from '../../services/locationService';
import { ApiError, TeamsResponse, TeamsState, baseTeamMemberInvitePayload, createTeamsPayload, updateTeamsPayload } from './locationTypes';
import { createLoationData, fetchLocationData } from '../../services/locationService';
import axios from 'axios';
import { BASE_URL } from '../../utils/api';


export const fetchLocationList = createAsyncThunk<
  TeamsResponse,
  number,
  { rejectValue: string }
>(
  'base/fetchLocationList',
  async (baseId, { rejectWithValue }) => {
    try {
      if (baseId === undefined) {
        throw new Error('Base ID is undefined');
      }
      const response = await fetchLocationData(baseId);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch base data');
    }
  }
);


// fetch location by status
export const fetchLocationListStatus = createAsyncThunk<
  TeamsResponse,
  { baseId: number; status?: string },
  { rejectValue: string }
>(
  'base/fetchLocationListStatus',
  async ({ baseId, status }, { rejectWithValue }) => {
    try {
      const response = await fetchLocationDataStatus(baseId, status);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch base data');
    }
  }
);


// create teams 
export const createLocation = createAsyncThunk<TeamsResponse, { payload: createTeamsPayload; userId: number },
  { rejectValue: string }
>(
  'base/createLocation',
  async ({ payload, userId }, { rejectWithValue }) => {
    try {
      const response = await createLoationData(payload, userId);
      return response;
    } catch (err) {
      const error = err as any;
      return rejectWithValue(error.response || 'Failed to invite member');
    }
  }
);




// fetch country 

export const fetchCountryList = createAsyncThunk(
  'location/fetchCountryList',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from local storage
      if (!token) {
        throw new Error('Authentication token is missing');
      }

      const response = await axios.get(`${BASE_URL}/location.countryList`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      return response.data;
    } catch (error: any) {
      console.error('Error fetching country list:', error);
      return rejectWithValue(error.response?.data?.message || 'An error occurred while fetching the country list');
    }
  }
);


// fetch states 


export const fetchStateList = createAsyncThunk(
  'location/fetchStateList',
  async (countryId, { rejectWithValue }) => { // Accept countryId as an argument
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing');
      }

      // Update the API endpoint to include the countryId
      const response = await axios.get(`${BASE_URL}/${countryId}/location.stateList`, { // Add countryId to the URL
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      return response.data;
    } catch (error: any) {
      console.error('Error fetching state list:', error);
      return rejectWithValue(error.response?.data?.message || 'An error occurred while fetching the state list');
    }
  }
);



//fetch city 
export const fetchCityList = createAsyncThunk(
  'location/fetchCityList',
  async (stateId, { rejectWithValue }) => { // Accept countryId as an argument
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing');
      }

      // Update the API endpoint to include the countryId
      const response = await axios.get(`${BASE_URL}/${stateId}/location.cityList`, { // Add countryId to the URL
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      return response.data;
    } catch (error: any) {
      console.error('Error fetching state list:', error);
      return rejectWithValue(error.response?.data?.message || 'An error occurred while fetching the state list');
    }
  }
);




// update teams
export const updateLocation = createAsyncThunk<TeamsResponse, { payload: updateTeamsPayload; userId: number },
  { rejectValue: string }
>(
  'base/updateLocation',
  async ({ payload, userId }, { rejectWithValue }) => {
    try {
      const response = await updateLocationData(userId, payload);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to invite member');
    }
  }
);

// delete teams

export const deleteTeams = createAsyncThunk<TeamsResponse, { userId: number; id: number }, { rejectValue: string }>(
  'base/deleteTeams',
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      // Ensure the parameters are in the correct order
      const response = await deleteTeamsData(userId, id);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to delete team');
    }
  }
);


export const fetchLocationByQuery = createAsyncThunk<
  TeamsResponse,
  string,
  { rejectValue: string }
>(
  'location/fetchLocationByQuery',
  async (searchQuery, { rejectWithValue }) => {
    try {
      const response = await fetchLocationBySearch(searchQuery);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch locations');
    }
  }
);


const initialState: TeamsState = {
  locationDetails: null,
  locationStatus: 'idle',
  locationStatusError: null,
  createTeamsStatus: 'idle',
  createTeamsError: null,
  createTeamsData: null,
  updateTeamsStatus: 'idle',
  updateTeamsError: null,
  updateTeamsData: null,
  deleteTeamsStatus: 'idle',
  deleteTeamsError: null,
  fetchTeamsByIdStatus: 'idle',
  fetchTeamsByIdError: null,
  fetchTeamsByIdData: null,
  baseMemberInviteStatus: 'idle',
  baseMemberInviteError: null,
  baseMemberInviteData: null,
  locationDetails1: null,
  locationStatus1: 'idle',
  locationStatusError1: null,

};

const locationSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLocationList.pending, (state) => {
        state.locationStatus = 'loading';
      })
      .addCase(fetchLocationList.fulfilled, (state, action: PayloadAction<TeamsResponse>) => {
        state.locationStatus = 'succeeded';
        state.locationDetails = action.payload.data;
        // localStorage.setItem('baseLocationData', JSON.stringify(action.payload));
      })
      .addCase(fetchLocationList.rejected, (state, action) => {
        state.locationStatus = 'failed';
        state.locationStatusError = action.payload || 'Failed to fetch boards';
      })
      .addCase(createLocation.pending, (state) => {
        state.createTeamsStatus = 'loading';
      })
      .addCase(createLocation.fulfilled, (state, action: PayloadAction<TeamsResponse>) => {
        state.createTeamsStatus = 'succeeded';
        state.createTeamsData = action.payload;
      })
      .addCase(createLocation.rejected, (state, action) => {
        state.createTeamsStatus = 'failed';
        state.createTeamsError = action.payload || 'Failed to invite member';
      })
      .addCase(updateLocation.pending, (state) => {
        state.updateTeamsStatus = 'loading';
      })
      .addCase(updateLocation.fulfilled, (state, action: PayloadAction<TeamsResponse>) => {
        state.updateTeamsStatus = 'succeeded';
        state.updateTeamsData = action.payload;
      })
      .addCase(updateLocation.rejected, (state, action) => {
        state.updateTeamsStatus = 'failed';
        state.updateTeamsError = action.payload || 'Failed to invite member';
      })
      .addCase(deleteTeams.pending, (state) => {
        state.deleteTeamsStatus = 'loading';
      })
      .addCase(deleteTeams.fulfilled, (state, action: PayloadAction<TeamsResponse>) => {
        state.deleteTeamsStatus = 'succeeded';
        state.updateTeamsData = action.payload;
      })
      .addCase(deleteTeams.rejected, (state, action) => {
        state.deleteTeamsStatus = 'failed';
        state.deleteTeamsError = action.payload || 'Failed to invite member';
      })
      .addCase(fetchLocationListStatus.pending, (state) => {
        state.locationStatus1 = 'loading';
      })
      .addCase(fetchLocationListStatus.fulfilled, (state, action: PayloadAction<TeamsResponse>) => {
        state.locationStatus1 = 'succeeded';
        state.locationDetails1 = action.payload.data;
        // localStorage.setItem('baseLocationData', JSON.stringify(action.payload));
      })
      .addCase(fetchLocationListStatus.rejected, (state, action) => {
        state.locationStatus1 = 'failed';
        state.locationStatusError1 = action.payload || 'Failed to fetch boards';
      })
    builder
      .addCase(fetchLocationByQuery.pending, (state) => {
        state.locationStatus = 'loading';
      })
      .addCase(fetchLocationByQuery.fulfilled, (state, action: PayloadAction<TeamsResponse>) => {        
        state.locationStatus = 'succeeded';
        state.locationDetails = action.payload.data;
      })
      .addCase(fetchLocationByQuery.rejected, (state, action) => {
        state.locationStatus = 'failed';
        state.locationStatusError = action.payload || 'Failed to fetch locations';
      });

  }
});

export default locationSlice.reducer;

